import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { ATOL_REGIONS } from 'constants/config/region'
import GeoContext from 'contexts/geoContext'
import { useAppSelector } from 'hooks/reduxHooks'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { rem } from 'polished'
import React, { useContext, useMemo, useCallback } from 'react'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { isSupportAssistantEnabled } from 'selectors/featuresSelectors'
import { styled } from 'styled-components'
import type { NavUspType } from './NavBar/NavBarUSP'
import NavBarUSP from './NavBar/NavBarUSP'
import NavBarChatButton from './NavBarButtons/NavBarChatButton'
import NavBarContactsButton from './NavBarButtons/NavBarContactsButton'
import NavBarStoreButton from './NavBarButtons/NavBarStoreButton'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import Pane from 'components/Common/Pane'
import switchRegion from 'lib/geo/switchRegion'
import TextLink from 'LuxKit/TextLink'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import Flag from 'LuxKit/Flag'
import LineSyncIcon from 'LuxKit/Icons/line/LineSyncIcon'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import config from 'constants/config'
import { FormattedMessage, useIntl } from 'react-intl'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'

const Container = styled(Pane)`
  width: 100%;
  color: ${props => props.theme.palette.neutral.default.three};
`

const TertiaryBar = styled(LayoutContainer)`
  display: flex;
  justify-content: center;
  padding: 0 ${rem(16)};

  ${mediaQueryUp.desktop} {
    justify-content: space-between;
    padding: 0 ${rem(20)};
  }
`

const USPSection = styled(Group)`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
  }
`

const DelimiterBullet = styled(BodyText)`
  &:first-child { display: none; }
`

interface Props {
  className?: string
}

function NavBarTertiaryBar(props: Props) {
  const { className } = props
  const intl = useIntl()
  const sep = intl.formatMessage(middleDotSeparatorMessage)
  const { currentRegionCode } = useContext(GeoContext)

  const chatEnabled = useAppSelector(isSupportAssistantEnabled)
  const loggedIn = useAppSelector(selectLoggedIn)
  const usps = useMemo<Array<NavUspType>>(() => {
    const usps: Array<NavUspType> = loggedIn ? [] : ['google', 'trustpilot']
    if (ATOL_REGIONS.includes(currentRegionCode)) {
      usps.unshift('atol')
    }
    return usps
  }, [currentRegionCode, loggedIn])

  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const [tier, tierName] = useCurrentLoyaltyTier()
  const loyaltyAccountNumber = useAppSelector(state => state.loyalty.account.data?.accountNumber)
  const loyaltyRegion = useAppSelector(getLoyaltyAccountRegion)
  const loyaltyCurrency = useMemo(() => {
    return config.regions.find(r => r.code === loyaltyRegion)?.currencyCode
  }, [loyaltyRegion])
  const isLoyaltyAccountInCorrectRegion = loyaltyRegion === currentRegionCode
  const showRegionSwitch = useMemo(() => {
    return loggedIn && !isLoyaltyAccountInCorrectRegion
  }, [loggedIn, isLoyaltyAccountInCorrectRegion])
  const handleSwitchRegion = useCallback(() => {
    if (!loyaltyRegion) return
    switchRegion(loyaltyRegion)
  }, [loyaltyRegion])

  const membershipLabel = intl.formatMessage(
    {
      id: 'u3b6a14bkU',
      defaultMessage: 'Membership {loyaltyAccountNumber}',
      description: 'Loyalty membership number display in tertiary navigation bar',
    },
    { loyaltyAccountNumber },
  )
  const switchBenefitsPrompt = intl.formatMessage(
    {
      id: 'SqDsU2zNJA',
      defaultMessage: 'To use your {programName} benefits',
      description: 'Prompt to switch region to use loyalty program benefits in tertiary bar',
    },
    { programName: getLoyaltyProgramDisplayName() },
  )
  const switchToLabel = loyaltyCurrency ? intl.formatMessage(
    {
      id: 'ok2kW0fJwP',
      defaultMessage: 'Switch to {loyaltyCurrency}',
      description: 'Link text to switch region to loyalty account currency',
    },
    { loyaltyCurrency },
  ) : ''

  return <ProductPaletteProvider palette={hasLoyaltyAccount ? `tier-${tier}` : 'default'}>
    <Container
      className={className}
      background={hasLoyaltyAccount ? `tier-${tier}-primary` : 'neutral-seven'}
    >
      <TertiaryBar variant="extraLarge">
        <USPSection direction="horizontal" verticalAlign="center" gap={8}>
          {hasLoyaltyAccount &&
            <>
              <Caption variant="large">
                <FormattedMessage
                  id="Zjv4K3WiLH"
                  defaultMessage="<tier>{tierName}</tier> {separator} {membershipLabel}"
                  description="Tertiary bar loyalty display combining tier name, separator, and membership number"
                  values={{
                    tierName,
                    separator: sep,
                    membershipLabel,
                    tier: (chunks: React.ReactNode) => <Caption variant="large" format="uppercase" weight="bold" as="span">{chunks}</Caption>,
                  }}
                />
              </Caption>
              {showRegionSwitch && loyaltyCurrency && <>
                {loyaltyRegion && <Flag size="S" countryCode={loyaltyRegion} />}
                <DelimiterBullet variant="small">{intl.formatMessage(middleDotSeparatorMessage)}</DelimiterBullet>
                <Caption variant="large">
                  {switchBenefitsPrompt}{' '}
                  <TextLink onClick={handleSwitchRegion}>
                    {' '}{switchToLabel}{' '}<LineSyncIcon />
                  </TextLink>
                </Caption>
              </>}
            </>}
          {usps.map((usp) => <React.Fragment key={usp}>
            <DelimiterBullet variant="small">{intl.formatMessage(middleDotSeparatorMessage)}</DelimiterBullet>
            <NavBarUSP type={usp} />
          </React.Fragment>)}
        </USPSection>
        <Group direction="horizontal" desktopGap={8}>
          <NavBarContactsButton textBreakpoint={usps.length > 2 ? 'desktop' : undefined} />
          {chatEnabled && <NavBarChatButton />}
          <NavBarStoreButton />
        </Group>
      </TertiaryBar>
    </Container>
  </ProductPaletteProvider>
}

export default NavBarTertiaryBar
